import type {
  GenerateQuizResponse,
  GetQuizletsResponse,
  GetQuizletResponse,
  SaveQuizletResponse,
  UpdateQuizletResponse,
  DeleteQuizletResponse,
  GetDailyKnowledgeQuestionsResponse,
  CreateSessionResponse,
  UpdateSessionProgressResponse,
  QuizLength,
  ListPendingQuizzesResponse,
  SavePendingQuizRequest,
  SavePendingQuizResponse,
  DeletePendingQuizResponse,
} from '@/types';
import type { NewQuizlet, UpdateQuizlet, QuizletFilters, Quizlet } from '@/types';
import type {
  DeckGroup,
  DeckGroupWithQuestions,
  DeckGroupFilters,
  CreateDeckGroupRequest,
  UpdateDeckGroupRequest,
} from '@/types/deck-group';
import type {
  Subscription,
  UserProfile,
  MonthlyQuizUsage,
  UsageStatus,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  CreatePortalSessionResponse,
} from '@/types/subscription';
import type {
  GlobalAnalyticsRequest,
  GlobalAnalyticsResponse,
  QuizletAnalyticsRequest,
  QuizletAnalyticsResponse,
  DailyKnowledgeAnalyticsRequest,
  DailyKnowledgeAnalyticsResponse,
  QuestionAnalyticsResponse,
  StreakDataResponse,
  InsightsRequest,
  InsightsResponse,
  SaveWidgetPreferencesRequest,
  SaveWidgetPreferencesResponse,
  WidgetPreferences,
} from '@/types/analytics';
import type {
  ListUsersParams,
  ListUsersResponse,
  AdminUserDetails,
  UpdateUserTierRequest,
  UpdateUserPermissionsRequest,
} from '@/types/admin';
import type {
  NewArticle,
  UpdateArticle,
  ArticleFilters,
  PublicArticleFilters,
  CreateArticleResponse,
  GetArticleResponse,
  ListArticlesResponse,
  UpdateArticleResponse,
  DeleteArticleResponse,
  ListTopicsResponse,
  ShareArticleResponse,
  ListSharesResponse,
  RecordViewResponse,
  ListCommentsResponse,
  AddCommentResponse,
  UpdateCommentResponse,
  DeleteCommentResponse,
  ArticleAnalyticsResponse,
  // Audio Recording types
  GetAudioUploadUrlRequest,
  GetAudioUploadUrlResponse,
  NewAudioRecording,
  CreateAudioRecordingResponse,
  ListAudioRecordingsResponse,
  GetAudioPlaybackUrlResponse,
  UpdateAudioRecording,
  UpdateAudioRecordingResponse,
  DeleteAudioRecordingResponse,
} from '@/types/article';
import type {
  CreateArticleCollectionRequest,
  UpdateArticleCollectionRequest,
  CreateArticleCollectionResponse,
  GetArticleCollectionResponse,
  ListArticleCollectionsResponse,
  GetCollectionTreeResponse,
  UpdateArticleCollectionResponse,
  DeleteArticleCollectionResponse,
  AddCollectionItemRequest,
  AddCollectionItemResponse,
  UpdateCollectionItemRequest,
  UpdateCollectionItemResponse,
  DeleteCollectionItemResponse,
  ReorderCollectionItemsRequest,
  ReorderCollectionItemsResponse,
  BulkAddArticlesRequest,
  BulkAddArticlesResponse,
} from '@/types/article-collection';
import { firebaseAuth } from '@/lib/firebase';
import { apiConfig, API_PATHS } from '@/config/api';

// Helper function for delays
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));


// API client class
class ApiClient {
  constructor() {
    // API client initialized
  }

  /**
   * Get current Firebase ID token for authenticated requests
   */
  private async getAuthToken(): Promise<string | null> {
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) {
      return null;
    }
    // Get Firebase ID token (automatically refreshes if expired)
    return await currentUser.getIdToken();
  }

  /**
   * Make authenticated API request
   */
  private async authenticatedRequest<T>(
    url: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = await this.getAuthToken();

    if (!token) {
      throw new Error('Not authenticated. Please sign in.');
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (response.status === 401) {
      // Token expired or invalid
      throw new Error('Session expired. Please sign in again.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || error.message || 'Request failed');
    }

    return response.json();
  }

  // Generate quiz from prompt
  async generateQuiz(
    prompt: string,
    length?: QuizLength,
    includeMath?: boolean,
    includeCode?: boolean,
    questionDistribution?: Record<string, number>,
    difficulty?: 'easy' | 'medium' | 'hard' | 'mixed'
  ): Promise<GenerateQuizResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.PROMPT.GENERATE_QUIZ}`;

    return this.authenticatedRequest<GenerateQuizResponse>(url, {
      method: 'POST',
      body: JSON.stringify({
        prompt,
        length,
        includeMath,
        includeCode,
        questionDistribution,
        difficulty
      }),
    });
  }

  // Get quiz generation job status
  async getQuizJobStatus(jobId: string): Promise<{
    status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
    quiz?: import('@/types').Quizlet;
    errorMessage?: string;
  }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.PROMPT.GET_STATUS(jobId)}`;
    return this.authenticatedRequest(url);
  }

  // ========================================================================
  // Pending Quiz Methods
  // ========================================================================

  /**
   * List all pending quizzes for the authenticated user
   */
  async listPendingQuizzes(): Promise<ListPendingQuizzesResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.PROMPT.PENDING_QUIZZES}`;
    return this.authenticatedRequest<ListPendingQuizzesResponse>(url);
  }

  /**
   * Save a pending quiz to the user's quizlets
   */
  async savePendingQuiz(id: string, data?: SavePendingQuizRequest): Promise<SavePendingQuizResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.PROMPT.SAVE_PENDING_QUIZ(id)}`;
    return this.authenticatedRequest<SavePendingQuizResponse>(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Discard a pending quiz
   */
  async deletePendingQuiz(id: string): Promise<DeletePendingQuizResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.PROMPT.DELETE_PENDING_QUIZ(id)}`;
    return this.authenticatedRequest<DeletePendingQuizResponse>(url, {
      method: 'DELETE',
    });
  }

  // Get all quizlets
  async getQuizlets(filters?: QuizletFilters): Promise<GetQuizletsResponse> {
    // Build query parameters
    const params = new URLSearchParams();
    if (filters?.is_daily_knowledge !== undefined) {
      params.append('is_daily_knowledge', String(filters.is_daily_knowledge));
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }

    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.QUIZLETS}${params.toString() ? `?${params.toString()}` : ''}`;

    const response = await this.authenticatedRequest<{ quizlets: Quizlet[]; total?: number }>(url);
    return { quizlets: response.quizlets };
  }

  // Get single quizlet
  async getQuizlet(id: string): Promise<GetQuizletResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.QUIZLET_BY_ID(id)}`;
    return this.authenticatedRequest<GetQuizletResponse>(url);
  }

  // Save new quizlet
  async saveQuizlet(data: NewQuizlet): Promise<SaveQuizletResponse> {
    // Questions are already in the correct format (NewQuestion[])
    const requestBody = {
      name: data.name,
      original_prompt: data.original_prompt,
      is_daily_knowledge: data.is_daily_knowledge || false,
      questions: data.questions,
    };

    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.QUIZLETS}`;
    return this.authenticatedRequest<SaveQuizletResponse>(url, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }

  // Update quizlet
  async updateQuizlet(id: string, data: UpdateQuizlet): Promise<UpdateQuizletResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.QUIZLET_BY_ID(id)}`;
    return this.authenticatedRequest<UpdateQuizletResponse>(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Delete quizlet
  async deleteQuizlet(id: string): Promise<DeleteQuizletResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.QUIZLET_BY_ID(id)}`;
    return this.authenticatedRequest<DeleteQuizletResponse>(url, {
      method: 'DELETE',
    });
  }

  // Get daily knowledge questions
  async getDailyKnowledgeQuestions(): Promise<GetDailyKnowledgeQuestionsResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.DAILY_KNOWLEDGE}`;
    return this.authenticatedRequest<GetDailyKnowledgeQuestionsResponse>(url);
  }

  // ========================================================================
  // Question Management Methods
  // ========================================================================

  // Add question to quizlet
  async addQuestion(quizletId: string, question: any): Promise<{ questionId: string }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.ADD_QUESTION(quizletId)}`;
    return this.authenticatedRequest<{ questionId: string }>(url, {
      method: 'POST',
      body: JSON.stringify(question),
    });
  }

  // Update question
  async updateQuestion(quizletId: string, questionId: string, question: any): Promise<{ success: boolean }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.UPDATE_QUESTION(quizletId, questionId)}`;
    return this.authenticatedRequest<{ success: boolean }>(url, {
      method: 'PUT',
      body: JSON.stringify(question),
    });
  }

  // Delete question
  async deleteQuestion(quizletId: string, questionId: string): Promise<{ success: boolean }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.DELETE_QUESTION(quizletId, questionId)}`;
    return this.authenticatedRequest<{ success: boolean }>(url, {
      method: 'DELETE',
    });
  }

  // Reorder questions
  async reorderQuestions(quizletId: string, questions: Array<{ id: string; order_index: number }>): Promise<{ success: boolean }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.REORDER_QUESTIONS(quizletId)}`;
    return this.authenticatedRequest<{ success: boolean }>(url, {
      method: 'PUT',
      body: JSON.stringify({ questions }),
    });
  }

  // ========================================================================
  // Quiz Session Tracking Methods
  // ========================================================================

  /**
   * Create a new quiz session
   */
  async createSession(data: { quizlet_id?: string; is_daily_knowledge: boolean }): Promise<CreateSessionResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.CREATE_SESSION}`;

    return this.authenticatedRequest<CreateSessionResponse>(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Record a question response in real-time
   */
  async recordResponse(
    sessionId: string,
    data: {
      question_id: string;
      user_answer: any;
      time_taken_seconds?: number;
    }
  ): Promise<import('@/types/session').RecordResponseResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.RECORD_RESPONSE(sessionId)}`;

    return this.authenticatedRequest<import('@/types/session').RecordResponseResponse>(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Complete a quiz session
   */
  async completeSession(sessionId: string): Promise<import('@/types/session').CompleteSessionResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.COMPLETE_SESSION(sessionId)}`;

    return this.authenticatedRequest<import('@/types/session').CompleteSessionResponse>(url, {
      method: 'PUT',
    });
  }

  /**
   * Update session progress (legacy method - kept for backward compatibility)
   * @deprecated Use recordResponse instead
   */
  async updateSessionProgress(
    sessionId: string,
    progress: { question_id: string; answered: boolean; correct?: boolean }
  ): Promise<UpdateSessionProgressResponse> {
    await delay(100);

    return {
      session: {
        id: sessionId,
        user_id: 'user-1',
        is_daily_knowledge: false,
        started_at: new Date().toISOString(),
        questions_attempted: 1,
        questions_correct: progress.correct ? 1 : 0,
        total_score: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    };
  }

  // ========================================================================
  // Subscription and Payment Methods
  // ========================================================================

  /**
   * Create a Stripe checkout session
   */
  async createCheckoutSession(
    request: CreateCheckoutSessionRequest
  ): Promise<CreateCheckoutSessionResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.SUBSCRIPTIONS.CHECKOUT}`;
    return this.authenticatedRequest<CreateCheckoutSessionResponse>(url, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Get current user's subscription
   */
  async getSubscription(): Promise<{ subscription: Subscription | null }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.SUBSCRIPTIONS.ME}`;
    return this.authenticatedRequest<{ subscription: Subscription | null }>(url);
  }

  /**
   * Get current user's profile
   */
  async getUserProfile(): Promise<{ profile: UserProfile | null; usage: MonthlyQuizUsage | null }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.SUBSCRIPTIONS.PROFILE_ME}`;
    return this.authenticatedRequest<{ profile: UserProfile | null; usage: MonthlyQuizUsage | null }>(url);
  }

  /**
   * Create user profile for Firebase authenticated users
   * Called after signup to create user_profiles entry in database
   */
  async createUserProfile(data: {
    display_name: string;
    first_name: string;
    last_name: string;
  }): Promise<{ profile: UserProfile; message: string }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.SUBSCRIPTIONS.PROFILE_CREATE}`;
    return this.authenticatedRequest<{ profile: UserProfile; message: string }>(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Check and increment daily usage limit
   */
  async checkUsageLimit(): Promise<UsageStatus> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.SUBSCRIPTIONS.USAGE_CHECK}`;
    return this.authenticatedRequest<UsageStatus>(url);
  }

  /**
   * Create a Stripe customer portal session
   */
  async createPortalSession(): Promise<CreatePortalSessionResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.SUBSCRIPTIONS.PORTAL}`;
    return this.authenticatedRequest<CreatePortalSessionResponse>(url, {
      method: 'POST',
    });
  }

  /**
   * Get total count of saved quizzes for the current user
   */
  async getQuizCount(): Promise<{ quizCount: number }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.SUBSCRIPTIONS.QUIZ_COUNT}`;
    return this.authenticatedRequest<{ quizCount: number }>(url);
  }

  // ========================================================================
  // Analytics Methods
  // ========================================================================

  /**
   * Get global analytics across all quizlets
   */
  async getGlobalAnalytics(request: GlobalAnalyticsRequest): Promise<GlobalAnalyticsResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ANALYTICS.GLOBAL}`;
    return this.authenticatedRequest<GlobalAnalyticsResponse>(url, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Get analytics for a specific quizlet
   */
  async getQuizletAnalytics(request: QuizletAnalyticsRequest): Promise<QuizletAnalyticsResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ANALYTICS.QUIZLET(request.quizletId)}`;
    return this.authenticatedRequest<QuizletAnalyticsResponse>(url, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Get Daily Knowledge analytics and streak data
   */
  async getDailyKnowledgeAnalytics(request: DailyKnowledgeAnalyticsRequest): Promise<DailyKnowledgeAnalyticsResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ANALYTICS.DAILY_KNOWLEDGE}`;
    return this.authenticatedRequest<DailyKnowledgeAnalyticsResponse>(url, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Get analytics for a specific question
   */
  async getQuestionAnalytics(questionId: string): Promise<QuestionAnalyticsResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ANALYTICS.QUESTION(questionId)}`;
    return this.authenticatedRequest<QuestionAnalyticsResponse>(url);
  }

  /**
   * Get Daily Knowledge streak data and milestones
   */
  async getStreakData(): Promise<StreakDataResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ANALYTICS.STREAK}`;
    return this.authenticatedRequest<StreakDataResponse>(url);
  }

  /**
   * Get automated insights and practice recommendations
   */
  async getInsights(request: InsightsRequest): Promise<InsightsResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ANALYTICS.INSIGHTS}`;
    return this.authenticatedRequest<InsightsResponse>(url, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Get user's widget preferences
   */
  async getWidgetPreferences(): Promise<{ preferences: WidgetPreferences[] }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ANALYTICS.PREFERENCES}`;
    return this.authenticatedRequest<{ preferences: WidgetPreferences[] }>(url);
  }

  /**
   * Save user's widget preferences
   */
  async saveWidgetPreferences(request: SaveWidgetPreferencesRequest): Promise<SaveWidgetPreferencesResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ANALYTICS.PREFERENCES}`;
    return this.authenticatedRequest<SaveWidgetPreferencesResponse>(url, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // ==================== Admin Methods ====================

  /**
   * List all users with pagination and search (admin only)
   */
  async listUsers(params: ListUsersParams): Promise<ListUsersResponse> {
    const queryParams = new URLSearchParams({
      page: String(params.page),
      limit: String(params.limit),
      ...(params.search && { search: params.search }),
      ...(params.tier && { tier: params.tier }),
      ...(params.adminOnly !== undefined && { admin_only: String(params.adminOnly) }),
    });

    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ADMIN.USERS}?${queryParams}`;
    return this.authenticatedRequest<ListUsersResponse>(url);
  }

  /**
   * Get detailed user information (admin only)
   */
  async getUserDetails(userId: string): Promise<AdminUserDetails> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ADMIN.USER_DETAILS(userId)}`;
    return this.authenticatedRequest<AdminUserDetails>(url);
  }

  /**
   * Update user's subscription tier (admin only)
   */
  async updateUserTier(request: UpdateUserTierRequest): Promise<{ success: boolean; message: string }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ADMIN.UPDATE_TIER(request.userId)}`;
    return this.authenticatedRequest<{ success: boolean; message: string }>(url, {
      method: 'PUT',
      body: JSON.stringify({ tier: request.tier, reason: request.reason }),
    });
  }

  /**
   * Update user permissions (admin only)
   */
  async updateUserPermissions(request: UpdateUserPermissionsRequest): Promise<{ success: boolean; message: string }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ADMIN.UPDATE_PERMISSIONS(request.userId)}`;
    return this.authenticatedRequest<{ success: boolean; message: string }>(url, {
      method: 'PUT',
      body: JSON.stringify({ permissions: request.permissions }),
    });
  }

  // ========================================================================
  // Deck Group Methods
  // ========================================================================

  /**
   * List all deck groups for the current user
   */
  async getDeckGroups(filters?: DeckGroupFilters): Promise<{ deckGroups: DeckGroup[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.search) {
      params.append('search', filters.search);
    }

    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.DECK_GROUPS}${params.toString() ? `?${params.toString()}` : ''}`;
    return this.authenticatedRequest<{ deckGroups: DeckGroup[]; total: number }>(url);
  }

  /**
   * Create a new deck group
   */
  async createDeckGroup(data: CreateDeckGroupRequest): Promise<{ deckGroupId: string }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.DECK_GROUPS}`;
    return this.authenticatedRequest<{ deckGroupId: string }>(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get a single deck group (metadata only)
   */
  async getDeckGroup(id: string): Promise<{ deckGroup: DeckGroup }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.DECK_GROUP_BY_ID(id)}`;
    return this.authenticatedRequest<{ deckGroup: DeckGroup }>(url);
  }

  /**
   * Update a deck group
   */
  async updateDeckGroup(id: string, data: UpdateDeckGroupRequest): Promise<{ deckGroup: DeckGroup }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.DECK_GROUP_BY_ID(id)}`;
    return this.authenticatedRequest<{ deckGroup: DeckGroup }>(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a deck group
   */
  async deleteDeckGroup(id: string): Promise<{ success: boolean }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.DECK_GROUP_BY_ID(id)}`;
    return this.authenticatedRequest<{ success: boolean }>(url, {
      method: 'DELETE',
    });
  }

  /**
   * Get deck group with full quiz data for starting a session
   */
  async startDeckGroupSession(id: string): Promise<{ deckGroup: DeckGroupWithQuestions }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.QUIZ.DECK_GROUP_START(id)}`;
    return this.authenticatedRequest<{ deckGroup: DeckGroupWithQuestions }>(url);
  }

  // ========================================================================
  // Article Methods
  // ========================================================================

  /**
   * Get all articles for the current user (including shared with them)
   */
  async getArticles(filters?: ArticleFilters): Promise<ListArticlesResponse> {
    const params = new URLSearchParams();
    if (filters?.search) {
      params.append('search', filters.search);
    }
    if (filters?.topic) {
      params.append('topic', filters.topic);
    }
    if (filters?.is_public !== undefined) {
      params.append('is_public', String(filters.is_public));
    }
    if (filters?.limit) {
      params.append('limit', String(filters.limit));
    }
    if (filters?.offset) {
      params.append('offset', String(filters.offset));
    }

    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.ARTICLES}${params.toString() ? `?${params.toString()}` : ''}`;
    return this.authenticatedRequest<ListArticlesResponse>(url);
  }

  /**
   * Get a single article by ID
   */
  async getArticle(id: string): Promise<GetArticleResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.ARTICLE_BY_ID(id)}`;
    return this.authenticatedRequest<GetArticleResponse>(url);
  }

  /**
   * Get public articles for browsing
   */
  async getPublicArticles(filters?: PublicArticleFilters): Promise<ListArticlesResponse> {
    const params = new URLSearchParams();
    if (filters?.search) {
      params.append('search', filters.search);
    }
    if (filters?.topic) {
      params.append('topic', filters.topic);
    }
    if (filters?.tags && filters.tags.length > 0) {
      params.append('tags', filters.tags.join(','));
    }
    if (filters?.limit) {
      params.append('limit', String(filters.limit));
    }
    if (filters?.offset) {
      params.append('offset', String(filters.offset));
    }

    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.PUBLIC}${params.toString() ? `?${params.toString()}` : ''}`;
    return this.authenticatedRequest<ListArticlesResponse>(url);
  }

  /**
   * Create a new article
   */
  async createArticle(data: NewArticle): Promise<CreateArticleResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.ARTICLES}`;
    return this.authenticatedRequest<CreateArticleResponse>(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update an article
   */
  async updateArticle(id: string, data: UpdateArticle): Promise<UpdateArticleResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.ARTICLE_BY_ID(id)}`;
    return this.authenticatedRequest<UpdateArticleResponse>(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete an article
   */
  async deleteArticle(id: string): Promise<DeleteArticleResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.ARTICLE_BY_ID(id)}`;
    return this.authenticatedRequest<DeleteArticleResponse>(url, {
      method: 'DELETE',
    });
  }

  /**
   * Get available article topics
   */
  async getTopics(): Promise<ListTopicsResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.TOPICS}`;
    return this.authenticatedRequest<ListTopicsResponse>(url);
  }

  /**
   * Share an article with a user by email
   */
  async shareArticle(articleId: string, email: string): Promise<ShareArticleResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.SHARE(articleId)}`;
    return this.authenticatedRequest<ShareArticleResponse>(url, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /**
   * Remove share access from a user
   */
  async removeArticleShare(articleId: string, userId: string): Promise<{ success: boolean }> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.REMOVE_SHARE(articleId, userId)}`;
    return this.authenticatedRequest<{ success: boolean }>(url, {
      method: 'DELETE',
    });
  }

  /**
   * List shares for an article
   */
  async getArticleShares(articleId: string): Promise<ListSharesResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.LIST_SHARES(articleId)}`;
    return this.authenticatedRequest<ListSharesResponse>(url);
  }

  /**
   * Record a view for an article
   */
  async recordArticleView(articleId: string): Promise<RecordViewResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.RECORD_VIEW(articleId)}`;
    return this.authenticatedRequest<RecordViewResponse>(url, {
      method: 'POST',
    });
  }

  /**
   * Get comments for an article
   */
  async getArticleComments(articleId: string): Promise<ListCommentsResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.COMMENTS(articleId)}`;
    return this.authenticatedRequest<ListCommentsResponse>(url);
  }

  /**
   * Add a comment to an article
   */
  async addArticleComment(articleId: string, content: string): Promise<AddCommentResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.COMMENTS(articleId)}`;
    return this.authenticatedRequest<AddCommentResponse>(url, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  /**
   * Update a comment
   */
  async updateArticleComment(articleId: string, commentId: string, content: string): Promise<UpdateCommentResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.COMMENT_BY_ID(articleId, commentId)}`;
    return this.authenticatedRequest<UpdateCommentResponse>(url, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
  }

  /**
   * Delete a comment
   */
  async deleteArticleComment(articleId: string, commentId: string): Promise<DeleteCommentResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.COMMENT_BY_ID(articleId, commentId)}`;
    return this.authenticatedRequest<DeleteCommentResponse>(url, {
      method: 'DELETE',
    });
  }

  /**
   * Get article analytics for the current user
   */
  async getArticleAnalytics(): Promise<ArticleAnalyticsResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.ANALYTICS}`;
    return this.authenticatedRequest<ArticleAnalyticsResponse>(url);
  }

  // ========================================================================
  // Audio Recording Methods
  // ========================================================================

  /**
   * Get a presigned URL for uploading an audio recording to S3
   */
  async getAudioUploadUrl(
    articleId: string,
    data: GetAudioUploadUrlRequest
  ): Promise<GetAudioUploadUrlResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.RECORDING_UPLOAD_URL(articleId)}`;
    return this.authenticatedRequest<GetAudioUploadUrlResponse>(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Create an audio recording after successful S3 upload
   */
  async createAudioRecording(
    articleId: string,
    data: NewAudioRecording
  ): Promise<CreateAudioRecordingResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.RECORDINGS(articleId)}`;
    return this.authenticatedRequest<CreateAudioRecordingResponse>(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get all audio recordings for an article
   */
  async getAudioRecordings(articleId: string): Promise<ListAudioRecordingsResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.RECORDINGS(articleId)}`;
    return this.authenticatedRequest<ListAudioRecordingsResponse>(url);
  }

  /**
   * Get a presigned URL for playing an audio recording
   */
  async getAudioPlaybackUrl(
    articleId: string,
    recordingId: string
  ): Promise<GetAudioPlaybackUrlResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.RECORDING_PLAYBACK_URL(articleId, recordingId)}`;
    return this.authenticatedRequest<GetAudioPlaybackUrlResponse>(url);
  }

  /**
   * Update an audio recording's metadata
   */
  async updateAudioRecording(
    articleId: string,
    recordingId: string,
    data: UpdateAudioRecording
  ): Promise<UpdateAudioRecordingResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.RECORDING_BY_ID(articleId, recordingId)}`;
    return this.authenticatedRequest<UpdateAudioRecordingResponse>(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete an audio recording
   */
  async deleteAudioRecording(
    articleId: string,
    recordingId: string
  ): Promise<DeleteAudioRecordingResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.RECORDING_BY_ID(articleId, recordingId)}`;
    return this.authenticatedRequest<DeleteAudioRecordingResponse>(url, {
      method: 'DELETE',
    });
  }

  // ========================================================================
  // Article Collection Methods
  // ========================================================================

  /**
   * Get all collections for the current user
   */
  async getArticleCollections(): Promise<ListArticleCollectionsResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.COLLECTIONS}`;
    return this.authenticatedRequest<ListArticleCollectionsResponse>(url);
  }

  /**
   * Get a single collection with its items
   */
  async getArticleCollection(id: string): Promise<GetArticleCollectionResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.COLLECTION_BY_ID(id)}`;
    return this.authenticatedRequest<GetArticleCollectionResponse>(url);
  }

  /**
   * Get the tree structure for a collection (for reading sidebar)
   */
  async getCollectionTree(collectionId: string): Promise<GetCollectionTreeResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.COLLECTION_TREE(collectionId)}`;
    return this.authenticatedRequest<GetCollectionTreeResponse>(url);
  }

  /**
   * Create a new collection
   */
  async createArticleCollection(
    data: CreateArticleCollectionRequest
  ): Promise<CreateArticleCollectionResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.COLLECTIONS}`;
    return this.authenticatedRequest<CreateArticleCollectionResponse>(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update a collection
   */
  async updateArticleCollection(
    id: string,
    data: UpdateArticleCollectionRequest
  ): Promise<UpdateArticleCollectionResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.COLLECTION_BY_ID(id)}`;
    return this.authenticatedRequest<UpdateArticleCollectionResponse>(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a collection
   */
  async deleteArticleCollection(id: string): Promise<DeleteArticleCollectionResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.COLLECTION_BY_ID(id)}`;
    return this.authenticatedRequest<DeleteArticleCollectionResponse>(url, {
      method: 'DELETE',
    });
  }

  // ========================================================================
  // Collection Item Methods
  // ========================================================================

  /**
   * Add an item (article or heading) to a collection
   */
  async addCollectionItem(
    collectionId: string,
    data: AddCollectionItemRequest
  ): Promise<AddCollectionItemResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.COLLECTION_ITEMS(collectionId)}`;
    return this.authenticatedRequest<AddCollectionItemResponse>(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update a collection item
   */
  async updateCollectionItem(
    collectionId: string,
    itemId: string,
    data: UpdateCollectionItemRequest
  ): Promise<UpdateCollectionItemResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.COLLECTION_ITEM_BY_ID(collectionId, itemId)}`;
    return this.authenticatedRequest<UpdateCollectionItemResponse>(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a collection item
   */
  async deleteCollectionItem(
    collectionId: string,
    itemId: string
  ): Promise<DeleteCollectionItemResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.COLLECTION_ITEM_BY_ID(collectionId, itemId)}`;
    return this.authenticatedRequest<DeleteCollectionItemResponse>(url, {
      method: 'DELETE',
    });
  }

  /**
   * Reorder items within a collection
   */
  async reorderCollectionItems(
    collectionId: string,
    data: ReorderCollectionItemsRequest
  ): Promise<ReorderCollectionItemsResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.COLLECTION_ITEMS_REORDER(collectionId)}`;
    return this.authenticatedRequest<ReorderCollectionItemsResponse>(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Bulk add articles to a collection
   */
  async bulkAddArticlesToCollection(
    collectionId: string,
    data: BulkAddArticlesRequest
  ): Promise<BulkAddArticlesResponse> {
    const url = `${apiConfig.getBaseUrl()}${API_PATHS.ARTICLES.COLLECTION_ITEMS_BULK_ADD(collectionId)}`;
    return this.authenticatedRequest<BulkAddArticlesResponse>(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

// Export singleton instance
export const api = new ApiClient();
